import {allConfigs} from './allConfigs';
import {
  cloudflareChallengeSiteKeyA2BByEnvironment,
  cloudflareChallengeSiteKeyByEnvironment,
} from './captchaKeyByEnvironment';
import {cmsUrlByEnvironment} from './cmsUrlByEnvironment';
import {Environment} from './environment';
import {getAyOutletUrlByLocale} from './getAyOutletUrlByLocale';
import {getCustomerEmailByLocale} from './getCustomerEmailByLocale';
import {Locale} from './locale';
import {nativeLanguageByLocale} from './nativeLanguageByLocale';
import {publicPortByEnvironment} from './publicPortByEnvironment';
import {publicProtocolByEnvironment} from './publicProtocolByEnvironment';
import {desktopShopIdConfig, mobileShopIdConfig} from './shopConfigByLocale';
import {getTadaridaRestUrlByEnvironment} from './urlMappings/getTadaridaRestUrlByEnvironment';
import {
  gripsUrlByEnvironment,
  tadaridaUrlByEnvironment,
} from './urlMappings/grpcUrlByEnvironment';
import type {HostConfig} from './urlMappings/types';

/**
 * This is marked UNSAFE since this is never intended to be used on the browser bundle.
 * This is intended to be used only on SSR to generate static config
 */
export function getHostConfigByHostname_UNSAFE(
  currentHostname: string,
): HostConfig<Locale, Environment> | undefined {
  let urlConfig = null;
  let urlSet = null;

  for (const environment of allConfigs) {
    urlSet = environment.urlSets.find(
      set =>
        set.hostname === currentHostname ||
        set.hostname === process.env.HOSTNAME_OVERRIDE,
    );
    if (urlSet) {
      urlConfig = environment;
      break;
    }
  }

  if (!urlConfig) {
    const featureBranchConfig = generateFeatureBranchConfig(currentHostname);

    if (featureBranchConfig.urlConfig && featureBranchConfig.urlSet) {
      urlConfig = featureBranchConfig.urlConfig;
      urlSet = featureBranchConfig.urlSet;
    }
  }

  if (!urlConfig || !urlSet) {
    return undefined;
  }

  const device = urlSet.device;

  const {shopId, childShopId} =
    device === 'desktop'
      ? desktopShopIdConfig[urlConfig.locale]
      : mobileShopIdConfig[urlConfig.locale];

  const {urlSets, ...clearedUrlConfig} = urlConfig;
  const {help_center_GET, sms_POST, ...clearedUrlSet} = urlSet;

  let isPasswordProtected = false;
  // isPasswordProtected is optional, but if we want to protect a host before go-live, we can set it to true in the config.urlSet for that locale
  if (urlSet.isPasswordProtected !== undefined) {
    isPasswordProtected = urlSet.isPasswordProtected;
  } else {
    // if isPasswordProtected is not set in the config.urlSet, we set it to true for all environments except production and development.
    // ie: staging, preview, feature branches, etc.
    isPasswordProtected = !['production', 'development'].includes(
      urlConfig.environment,
    );
  }

  return {
    ...clearedUrlConfig,
    ...clearedUrlSet,
    isPasswordProtected,
    publicPort: publicPortByEnvironment[urlConfig.environment][device],
    publicProtocol: publicProtocolByEnvironment[urlConfig.environment],
    tadaridaUrl: tadaridaUrlByEnvironment[urlConfig.environment],
    tadaridaRestUrl: getTadaridaRestUrlByEnvironment(urlConfig.environment),
    gripsUrl: gripsUrlByEnvironment[urlConfig.environment],
    ayOutletUrl: getAyOutletUrlByLocale(urlConfig.locale),
    customerEmail: getCustomerEmailByLocale(urlConfig.locale),
    nativeLanguage: nativeLanguageByLocale[urlConfig.locale],
    cloudflareChallengeSiteKey:
      cloudflareChallengeSiteKeyByEnvironment[urlConfig.environment],
    cloudflareChallengeDefaultSiteKey:
      cloudflareChallengeSiteKeyA2BByEnvironment[urlConfig.environment],
    legacyConfig: {
      shopId,
      childShopId,
      help_center_GET,
      sms_POST,
    },
    cmsUrl: cmsUrlByEnvironment[urlConfig.environment],
  };
}

/**
 * Generated a urlSet and urlConfig based on a feature branch. Those will not be added to the static config but generated based on patterns. We always expect the feature branch url to contain a subdomain in front of a normal host defined in the config, e.g. `ayops-774.m-fr.aboutyou-staging.ch` relates to `m-fr.aboutyou-staging.ch`.
 * This function reused the original config and tweaks some config so that it matches the requirement of the feature branch.
 */
export const generateFeatureBranchConfig = (
  currentHostname: string | undefined,
) => {
  let urlSet;
  let urlConfig;

  // Guard: an undefined or empty hostname cannot match any feature-branch pattern.
  if (!currentHostname) {
    return {urlSet, urlConfig};
  }

  const potentialOriginalHostname =
    getMainHostnameForBranchBasedStagingHostnames(currentHostname);

  for (const environment of allConfigs) {
    urlSet = environment.urlSets.find(
      set => set.hostname === potentialOriginalHostname,
    );
    if (urlSet) {
      if (urlSet.assetsUrl) {
        urlSet = {
          ...urlSet,
          // feature branches are not supported to use the assets.aboutyou-staging.com domain for static assets
          assetsUrl: undefined,
        };
      }
      urlConfig = environment;
      break;
    }
  }

  return {
    urlSet,
    urlConfig,
  };
};

/**
 * For testing purposes we allow to have any branch deployed to a staging/ preview system. The urls for the domain will still have a similar pattern as before, but with the branch name upfront, divided by a dot.
 * @example `AYOPS-774-custo-m-branch-name.m.aboutyou-staging.de` -> `m.aboutyou-staging.de`
 *
 */
export const getMainHostnameForBranchBasedStagingHostnames = (
  hostname: string | undefined,
): string => {
  if (!hostname) {
    return '';
  }
  const [, ...hostnameParts] = hostname.split('.');
  return hostnameParts.join('.');
};
